import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../index';

import { getClientInfo } from 'api/clients';
import {
    getAllTrialRequests,
    approveTrialRequest,
    denyTrialRequest,
    sendTrialNotification,
    getTrialRequestsSummary,
    saveTrialRequest
} from 'api/trial-requests';
import { store } from 'store';

export const initialState = {
    list: [],
    total: 0,
    error: [],
    isLoading: false,
    rowsPerPage: 10,
    currentPage: 1,
    searchQuery: '',
    renewalDateFilter: null,
    summary: {
        active: 0,
        declined: 0,
        approved: 0,
        isLoading: false
    },
    selected: {
        data: null,
        isLoading: false
    },
    sorting: {
        field: 'createdAt',
        value: -1 // 1 = asc; -1 = desc
    },
    tags: []
};

const slice = createSlice({
    name: 'trialRequests',
    initialState,
    reducers: {
        setTrialRequestsList(state, action) {
            state.list = action.payload.data;
            state.total = action.payload.total;
            state.isLoading = false;
        },
        setIsLoading(state, action) {
            state.isLoading = action.payload;
            // reset error if loading is true;
            if (action.payload === true) state.error = [];
        },
        setErrors(state, action) {
            state.error = action.payload;
        },
        setTrialRequestsCurrentPage(state, action) {
            state.currentPage = action.payload;
        },
        setSearchQuery(state, action) {
            state.currentPage = 1; // reset to page 1
            state.searchQuery = action.payload;
        },
        setTrialRequestsRowsPerPage(state, action) {
            state.rowsPerPage = action.payload;
        },
        resetTrialRequestState() {
            return initialState;
        },

        setTrialRequestsSummaryLoading(state, action) {
            state.summary.isLoading = action.payload;
        },

        setTrialRequestsSummary(state, action) {
            state.summary = action.payload.data;
            state.summary.isLoading = false;
        },
        setSelectedTrialClientState(state, action) {
            state.selected = action.payload;
        },
        setSorting(state, action) {
            state.currentPage = 1;
            state.sorting = action.payload;
        },
        setTags(state, action) {
            state.currentPage = 1; // reset to page 1
            state.tags = action.payload;
        }
    }
});

export default slice.reducer;

export function fetchTrialRequestsList() {
    return async () => {
        await getListTrialRequests();
    };
}

export function initialLoadTrialRequestData() {
    return async () => {
        await dispatch(slice.actions.resetTrialRequestState());
        await getListTrialRequests();
    };
}

export function trialRequestsCurrentPage(val) {
    return async () => {
        await dispatch(slice.actions.setTrialRequestsCurrentPage(val));
        await getListTrialRequests();
    };
}

export function trialRequestsRowsPerPage(val) {
    return async () => {
        dispatch(slice.actions.setTrialRequestsRowsPerPage(val));
    };
}

export function searchTrialRequestsList(query) {
    return async () => {
        dispatch(slice.actions.setSearchQuery(query));
        await getListTrialRequests();
    };
}

export function fetchTrialRequestsSummary() {
    return async () => {
        await getTrialRequestSummary();
    };
}

export function processApproveRequest(id) {
    return async () => {
        try {
            await approveTrialRequest(id);
            getListTrialRequests();
            getTrialRequestSummary();
        } catch (err) {
            console.log(err);
            dispatch(slice.actions.setTrialRequestsSummaryLoading(false));
            throw new Error(err.message);
        }
    };
}

export function processDenyRequest(id, reason) {
    return async () => {
        try {
            await denyTrialRequest(id, reason);
            getListTrialRequests();
            getTrialRequestSummary();
        } catch (err) {
            console.log(err);
            throw new Error(err.message);
        }
    };
}

export function sendTrialAccountNotification(deployment, type) {
    return async () => {
        try {
            await sendTrialNotification(deployment, type);
            getSelectedTrialData(deployment);
        } catch (err) {
            console.log(err);
            throw new Error(err.message);
        }
    };
}

export function setSelectedTrialClient(deployment) {
    return async () => {
        await getSelectedTrialData(deployment);
    };
}

export function saveNewTrialRequest(data) {
    return async () => {
        try {
            const response = await saveTrialRequest(data);

            getListTrialRequests();
            getTrialRequestSummary();
            return response;
        } catch (e) {
            throw new Error(e.message);
        }
    };
}

export function setTrialRequestSorting(val) {
    return async () => {
        dispatch(slice.actions.setSorting(val));
        await getListTrialRequests();
    };
}

// clients list advanced filtering
export function searchAdvancedFiltering(tags) {
    return async () => {
        dispatch(slice.actions.setTags(tags));
        await getListTrialRequests();
    };
}

async function getListTrialRequests() {
    try {
        dispatch(slice.actions.setIsLoading(true));
        const storeData = store.getState();
        const { currentPage, rowsPerPage, searchQuery, renewalDateFilter, sorting, tags } = storeData.trialRequests;
        const trialRequests = await getAllTrialRequests({
            perPage: rowsPerPage,
            pageNo: currentPage,
            query: searchQuery,
            startDate: renewalDateFilter?.startDate || '',
            endDate: renewalDateFilter?.endDate || '',
            sortField: sorting?.field,
            sorting: sorting.value,
            tags
        });
        dispatch(slice.actions.setTrialRequestsList(trialRequests?.data));
    } catch (err) {
        console.log(err);
        dispatch(slice.actions.setIsLoading(false));
        throw new Error(err.message);
    }
}

async function getTrialRequestSummary() {
    try {
        dispatch(slice.actions.setTrialRequestsSummaryLoading(true));
        const summary = await getTrialRequestsSummary();
        dispatch(slice.actions.setTrialRequestsSummary(summary));
    } catch (err) {
        console.log(err);
        dispatch(slice.actions.setTrialRequestsSummaryLoading(false));
        throw new Error(err.message);
    }
}

async function getSelectedTrialData(deployment) {
    dispatch(slice.actions.setSelectedTrialClientState({ data: {}, isLoading: true }));
    const clientInfo = await getClientInfo(deployment);
    dispatch(slice.actions.setSelectedTrialClientState({ data: clientInfo?.data, isLoading: false }));
}
